import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { CornerDownRight, Eye, EyeOff, MessageSquare, Reply } from 'lucide-react';
import { adminQueries, getErrorMessage, keys } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatDateTime, formatNumber, hasPermission } from '@blimo/shared';
import {
  Badge,
  Button,
  CommentForm,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  PageHeader,
  SearchInput,
  toast,
} from '@blimo/ui';
import { FilterBar, FilterSelect } from '../../components/filter-bar.jsx';
import { PropertyFilter } from '../finance/panels.jsx';
import { useListState } from '../../lib/list-search.js';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Sichtbar' },
  { value: 'hidden', label: 'Ausgeblendet' },
];
const KIND_OPTIONS = [
  { value: 'thread', label: 'Nur Kommentare' },
  { value: 'reply', label: 'Nur Antworten' },
];
const REPLIES_OPTIONS = [
  { value: '1', label: 'Mit Antworten' },
  { value: '0', label: 'Ohne Antworten' },
];
const YEAR_OPTIONS = Array.from({ length: 12 }, (_, i) => String(new Date().getFullYear() - i)).map((y) => ({ value: y, label: y }));

/**
 * Kommunikation › Kommentare (F-505, `comments.moderate`): alle Kommentare der Belegprüfungen, neueste zuerst.
 * Aus-/Einblenden und Antworten nutzen die Endpunkte der Belegprüfung; Antworten nur mit `document-checks.manage`.
 */
export function CommentsModerationPage() {
  const api = useApi();
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const search = useSearch({ strict: false });
  const list = useListState(search);
  const [replyTo, setReplyTo] = useState(/** @type {any} */ (null));
  const filter = {
    q: search.q,
    property_id: search.property,
    fiscal_year: search.year,
    status: search.status,
    kind: search.kind,
    has_replies: search.replies,
    author_id: search.author,
  };
  const params = { page: list.apiParams.page, per_page: list.apiParams.per_page, filter };
  const { data, error, isPending, isFetching, refetch } = useQuery(adminQueries.comments(api, params));
  const canReply = hasPermission(me, 'document-checks.manage');

  const refresh = (comment) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: keys.admin.resource('comments') }),
      queryClient.invalidateQueries({ queryKey: keys.admin.sub('document-checks', comment.document_check.id, 'comments') }),
    ]);

  async function moderate(comment) {
    try {
      await api.admin.documentChecks.moderateComment(comment.document_check.id, comment.id, !comment.is_active);
      await refresh(comment);
      toast.success(comment.is_active ? 'Kommentar ausgeblendet' : 'Kommentar wieder eingeblendet');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Der Kommentar konnte nicht geändert werden.'));
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps -- handlers only depend on stable api/queryClient
  const columns = useMemo(() => commentColumns({ moderate, onReply: canReply ? setReplyTo : null }), [canReply]);
  const authors = (data?.meta?.authors ?? []).map((a) => ({ value: String(a.id), label: a.name }));

  return (
    <>
      <PageHeader title="Kommentare" description="Alle Kommentare und Antworten aus den Belegprüfungen – moderieren, beantworten und zur Prüfung springen." />
      <FilterBar>
        <SearchInput label="Kommentare durchsuchen" placeholder="Text …" value={search.q ?? ''} onValueChange={(v) => list.setFilter('q', v)} />
        <PropertyFilter value={search.property} onChange={(v) => list.setFilter('property', v)} />
        <FilterSelect label="Abrechnungsjahr" value={search.year} onChange={(v) => list.setFilter('year', v)} options={YEAR_OPTIONS} />
        <FilterSelect label="Status" value={search.status} onChange={(v) => list.setFilter('status', v)} options={STATUS_OPTIONS} />
        <FilterSelect label="Art" value={search.kind} onChange={(v) => list.setFilter('kind', v)} options={KIND_OPTIONS} />
        <FilterSelect label="Antworten" value={search.replies} onChange={(v) => list.setFilter('replies', v)} options={REPLIES_OPTIONS} />
        <FilterSelect label="Verfasser" value={search.author} onChange={(v) => list.setFilter('author', v)} options={authors} />
      </FilterBar>
      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <DataTable
          label="Kommentare"
          columns={columns}
          data={data?.data}
          rowCount={data?.meta?.total}
          isLoading={isPending}
          isFetching={isFetching}
          getRowId={(row) => String(row.id)}
          pagination={list.pagination}
          onPaginationChange={list.onPaginationChange}
          emptyState={
            <EmptyState
              compact
              className="border-0"
              icon={MessageSquare}
              title="Keine Kommentare"
              description="Für die gewählten Filter liegen keine Kommentare vor."
            />
          }
        />
      )}
      <ReplyDialog comment={replyTo} onClose={() => setReplyTo(null)} onSent={refresh} />
    </>
  );
}

const checkLabel = (c) => `${c.document_check.property?.name ?? 'Liegenschaft'} · ${c.document_check.fiscal_year}`;

/** @param {{ moderate: (c: any) => void, onReply: ((c: any) => void) | null }} handlers */
function commentColumns({ moderate, onReply }) {
  return [
    {
      id: 'created_at',
      header: 'Datum',
      enableSorting: false,
      cell: ({ row }) => <span className="numeric whitespace-nowrap">{formatDateTime(row.original.created_at)}</span>,
    },
    {
      id: 'check',
      header: 'Belegprüfung',
      enableSorting: false,
      cell: ({ row }) => (
        <span className="flex flex-col">
          <Link
            to="/belegpruefung/$checkId"
            params={{ checkId: String(row.original.document_check.id) }}
            search={{ tab: 'kommentare' }}
            className="font-medium text-primary hover:underline"
          >
            {checkLabel(row.original)}
          </Link>
          {row.original.document && (
            <span className="text-xs text-muted-foreground">
              {row.original.document.name}
              {row.original.document.deleted && ' (gelöscht)'}
              {row.original.compare_document && ` ↔ ${row.original.compare_document.name}`}
            </span>
          )}
        </span>
      ),
    },
    {
      id: 'author',
      header: 'Verfasser',
      enableSorting: false,
      cell: ({ row }) => row.original.author?.name ?? <span className="text-muted-foreground">–</span>,
    },
    {
      id: 'body',
      header: 'Kommentar',
      enableSorting: false,
      meta: { cellClassName: 'max-w-md' },
      cell: ({ row }) => (
        <span className="flex flex-col gap-0.5">
          {row.original.parent && (
            <span className="flex items-start gap-1 text-xs text-muted-foreground">
              <CornerDownRight className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
              <span>
                Antwort auf {row.original.parent.author ?? 'Kommentar'}: „{row.original.parent.excerpt}“
              </span>
            </span>
          )}
          <span className={row.original.is_active ? 'line-clamp-3 whitespace-pre-line' : 'line-clamp-3 whitespace-pre-line text-muted-foreground line-through'}>
            {row.original.body}
          </span>
        </span>
      ),
    },
    {
      id: 'replies',
      header: 'Antworten',
      enableSorting: false,
      meta: { numeric: true },
      cell: ({ row }) => (row.original.parent_id ? <span className="text-muted-foreground">–</span> : formatNumber(row.original.replies_count)),
    },
    {
      id: 'status',
      header: 'Status',
      enableSorting: false,
      cell: ({ row }) => (row.original.is_active ? <Badge variant="success">Sichtbar</Badge> : <Badge variant="warning">Ausgeblendet</Badge>),
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">Aktionen</span>,
      enableSorting: false,
      cell: ({ row }) => {
        const c = row.original;
        return (
          <span className="flex justify-end gap-1">
            {onReply && c.document && !c.document.deleted && (
              <Button size="sm" variant="ghost" onClick={() => onReply(c)} aria-label={`Auf Kommentar #${c.id} antworten`}>
                <Reply aria-hidden="true" /> Antworten
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => moderate(c)} aria-label={`Kommentar #${c.id} ${c.is_active ? 'ausblenden' : 'einblenden'}`}>
              {c.is_active ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
              {c.is_active ? 'Ausblenden' : 'Einblenden'}
            </Button>
          </span>
        );
      },
    },
  ];
}

/** Reply in the thread of the comment (replies to a reply go to its thread root). */
function ReplyDialog({ comment, onClose, onSent }) {
  const api = useApi();
  async function submit(body) {
    await api.admin.documentChecks.addComment(comment.document_check.id, {
      document_id: comment.document.id,
      compare_document_id: comment.compare_document?.id ?? null,
      parent_id: comment.parent_id ?? comment.id,
      body,
    });
    await onSent(comment);
    toast.success('Antwort gespeichert');
    onClose();
  }
  return (
    <Dialog open={Boolean(comment)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Antworten</DialogTitle>
          <DialogDescription>{comment ? `${checkLabel(comment)} · ${comment.author?.name ?? 'Kommentar'}` : ''}</DialogDescription>
        </DialogHeader>
        {comment && (
          <>
            <blockquote className="border-l-2 pl-3 text-sm whitespace-pre-line text-muted-foreground">{comment.parent?.excerpt ?? comment.body}</blockquote>
            <CommentForm label="Antwort" submitLabel="Antwort senden" onSubmit={submit} onCancel={onClose} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
